'use client';

import { Dialog } from 'primereact/dialog';
import { X, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';
import { useEffect } from 'react';
import type { Modal, NotificationVariant } from '@/lib/stores/notification-store';
import { useNotificationStore } from '@/lib/stores/notification-store';
import { cn } from '@/lib/utils';
import { Button } from './button';

// ============================================================================
// CONSTANTS
// ============================================================================

const VARIANT_CONFIG: Record<
  NotificationVariant,
  {
    Icon: typeof CheckCircle;
    iconClassName: string;
    bgClassName: string;
  }
> = {
  success: {
    Icon: CheckCircle,
    iconClassName: 'text-success',
    bgClassName: 'bg-success/10',
  },
  error: {
    Icon: XCircle,
    iconClassName: 'text-error',
    bgClassName: 'bg-error/10',
  },
  warning: {
    Icon: AlertTriangle,
    iconClassName: 'text-warning',
    bgClassName: 'bg-warning/10',
  },
  info: {
    Icon: Info,
    iconClassName: 'text-info',
    bgClassName: 'bg-info/10',
  },
};

const SIZE_CLASSES: Record<NonNullable<Modal['size']>, string> = {
  sm: 'w-[calc(100%-2rem)] sm:w-auto max-w-sm',
  md: 'w-[calc(100%-2rem)] sm:w-auto max-w-md',
  lg: 'w-[calc(100%-2rem)] sm:w-auto max-w-lg',
  xl: 'w-[calc(100%-2rem)] sm:w-auto max-w-xl',
  full: 'w-[calc(100%-2rem)] max-w-full',
};

// ============================================================================
// COMPONENT
// ============================================================================

export interface ModalProps {
  modal: Modal;
}

export function ModalComponent({ modal }: ModalProps) {
  const hideModal = useNotificationStore((state) => state.hideModal);

  const { Icon, iconClassName, bgClassName } = VARIANT_CONFIG[modal.variant];
  const sizeClass = SIZE_CLASSES[modal.size ?? 'md'];

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const getSeverity = (): 'success' | 'info' | 'warn' | 'danger' | null => {
    switch (modal.variant) {
      case 'success':
        return 'success';
      case 'error':
        return 'danger';
      case 'warning':
        return 'warn';
      case 'info':
        return 'info';
      default:
        return null;
    }
  };

  return (
    <Dialog
      visible={true}
      onHide={() => {
        if (modal.dismissible) {
          hideModal(modal.id);
        }
      }}
      modal
      dismissableMask={modal.dismissible}
      closable={false}
      className={cn('p-dialog-custom', sizeClass)}
      header={
        <div className={cn('flex items-start justify-between gap-4 pb-4', bgClassName)}>
          <div className="flex items-start gap-3">
            <div className="shrink-0">
              <Icon className={cn('h-6 w-6', iconClassName)} aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h3 id={`modal-title-${modal.id}`} className="text-lg font-semibold" style={{ color: "var(--p-text-color)" }}>{modal.title}</h3>
            </div>
          </div>
          {modal.dismissible && (
            <button
              type="button"
              onClick={() => hideModal(modal.id)}
              aria-label="Fechar modal"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[var(--p-border-color)] bg-white text-xs font-semibold shadow-sm transition-colors hover:bg-[var(--p-surface-100)] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </div>
      }
      footer={
        modal.actions && modal.actions.length > 0 ? (
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
            {modal.actions.map((action, index) => (
              <Button
                key={index}
                onClick={() => {
                  action.onClick();
                  if (action.closeOnClick !== false) {
                    hideModal(modal.id);
                  }
                }}
                variant={
                  action.variant === 'primary'
                    ? 'default'
                    : action.variant === 'danger'
                      ? 'destructive'
                      : action.variant === 'secondary'
                        ? 'secondary'
                        : 'ghost'
                }
                className="w-full sm:w-auto"
              >
                {action.label}
              </Button>
            ))}
          </div>
        ) : undefined
      }
    >
      <div className="px-5 py-4" role="document" aria-labelledby={`modal-title-${modal.id}`} aria-describedby={`modal-desc-${modal.id}`}>
        <p
          id={`modal-desc-${modal.id}`}
          className="text-sm whitespace-pre-line"
          style={{ color: "var(--p-text-color)" }}
        >
          {modal.description}
        </p>
      </div>
    </Dialog>
  );
}
